import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/Tabs';
import UploadPdfButton from '@/components/UploadPDFButton/UploadPdfButton';
import { useIsCrawler } from '@/hooks';

import * as icons from './Icons';
import Option from './Option';

const typeOptions = ['XLSX', 'XLS', 'DOCX', 'DOC', 'PPT', 'JPG', 'PNG', 'SVG'];

function Tools() {
	const { t, i18n } = useTranslation();
	const isCrawler = useIsCrawler();

	const sections = useMemo(() => {
		return [
			{
				name: 'editAndSign',
				options: [
					'convert',
					'organize',
					'signature',
					'share',
					'annotate',
					'editText',
					'editImage',
					'protect'
				].map(option => ({
					title: t(`home.tools.options.${option}`),
					image: icons[option]
				}))
			},
			{
				name: 'convertFromPdf',
				options: [
					...typeOptions.map(type => ({
						title: t('home.tools.options.fromTo', {
							fromType: 'PDF',
							toType: type
						}),
						image: icons[`to${type}`]
					})),
					{
						title: t('home.tools.options.fromTo', {
							fromType: 'PDF',
							toType: 'Image'
						}),
						image: icons.toImage
					}
				]
			},
			{
				name: 'convertToPdf',
				options: [
					...typeOptions.map(type => ({
						title: t('home.tools.options.fromTo', {
							fromType: type,
							toType: 'PDF'
						}),
						image: icons[`from${type}`]
					})),
					{
						title: t('home.tools.options.fromTo', {
							fromType: 'Image',
							toType: 'PDF'
						}),
						image: icons.fromImage
					}
				]
			}
		];
	}, [i18n.language, i18n.isInitialized]);

	const TabsContentComponent = isCrawler ? 'div' : TabsContent;

	return (
		<div className="container flex flex-col items-center gap-3">
			<h3 className="text-center font-title text-[32px] font-semibold leading-10 md:lacks-hover:text-[40px] md:lacks-hover:font-bold md:lacks-hover:leading-[52px] lg:text-[40px] lg:font-bold lg:leading-[52px]">
				{t('home.tools.title')}
			</h3>
			<h4 className="text-center text-sm leading-6 md:lacks-hover:text-base lg:text-base lg:leading-7">
				{t('home.tools.subtitle')}
			</h4>
			<div className="container mt-8 flex justify-center">
				<Tabs defaultValue="editAndSign">
					<TabsList className="mb-9 flex max-w-[calc(100vw-40px)] justify-start gap-4 overflow-auto whitespace-nowrap md:lacks-hover:mb-12 md:lacks-hover:justify-center lg:mb-[60px] lg:justify-center">
						{sections.map(({ name }) => {
							return (
								<TabsTrigger
									value={name}
									key={name}
									className="md:lacks-hover:px-4 md:lacks-hover:py-2 md:lacks-hover:text-base md:lacks-hover:leading-7"
								>
									{t(`home.tools.${name}`)}
								</TabsTrigger>
							);
						})}
					</TabsList>
					{sections.map(({ name, options }, index) => {
						return (
							<TabsContentComponent
								className="mb-6 flex flex-wrap justify-center gap-4 lg:gap-5 lg:can-hover:mb-[60px]"
								key={index}
								{...(!isCrawler && { value: name })}
							>
								{options.map(({ title, image }) => {
									return (
										<Option title={title} key={title} image={image} />
									);
								})}
							</TabsContentComponent>
						);
					})}
				</Tabs>
			</div>
			<UploadPdfButton className="md:lacks-hover:mt-6" />
		</div>
	);
}

export default Tools;
